import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { alarmAcknowledgedEvent } from '../services/events/alarmEvents';
import { getUltraCriticalAlarms } from '../services/bureauManagement/bureauManagement.service';
import type { BureauAlarm } from '../services/bureauManagement/bureauManagement.types';

interface UltraCriticalSummary {
  alarms: BureauAlarm[];
  totalCount: number;
  activeNow: number;
  newLast15Minutes: number;
  sitesAffected: number;
}

interface UseUltraCriticalAlarmsResult extends UltraCriticalSummary {
  hasAny: boolean;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

const EMPTY_SUMMARY: UltraCriticalSummary = {
  alarms: [],
  totalCount: 0,
  activeNow: 0,
  newLast15Minutes: 0,
  sitesAffected: 0,
};

function asNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return undefined;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function getAlarmsFromPayload(data: unknown): BureauAlarm[] {
  if (Array.isArray(data)) {
    return data as BureauAlarm[];
  }
  const record = asRecord(data);
  if (!record) {
    return [];
  }

  const candidates = [record.alarms, record.bureauAlarms, record.items];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate as BureauAlarm[];
    }
  }

  return [];
}

function getSiteKey(alarm: BureauAlarm): string | null {
  if (alarm.siteRegistryId !== null && alarm.siteRegistryId !== undefined) {
    return alarm.siteRegistryId.toString();
  }
  if (alarm.siteGuid) {
    return alarm.siteGuid;
  }
  if (alarm.siteIdentifier) {
    return alarm.siteIdentifier;
  }
  if (alarm.siteName) {
    return alarm.siteName;
  }
  return null;
}

function countDistinctSites(alarms: BureauAlarm[]): number {
  const unique = new Set<string>();
  alarms.forEach(alarm => {
    const key = getSiteKey(alarm);
    if (key) {
      unique.add(key);
    }
  });
  return unique.size;
}

function normalizeUltraCritical(data: unknown): UltraCriticalSummary {
  const alarms = getAlarmsFromPayload(data);
  const record = asRecord(data);

  const totalCount =
    asNumber(record?.totalCount) ??
    asNumber(record?.totalRecords) ??
    alarms.length;

  const activeNow =
    asNumber(record?.activeNow) ??
    totalCount;

  const newLast15Minutes =
    asNumber(record?.newLast15Minutes) ??
    asNumber(record?.newLast15Mins) ??
    asNumber(record?.last15MinCount) ??
    0;

  const sitesAffected =
    asNumber(record?.sitesAffected) ??
    asNumber(record?.sitesCount) ??
    countDistinctSites(alarms);

  return {
    alarms,
    totalCount,
    activeNow,
    newLast15Minutes,
    sitesAffected,
  };
}


export function useUltraCriticalAlarms(): UseUltraCriticalAlarmsResult {
  const auth = useAuth();
  const [summary, setSummary] = useState<UltraCriticalSummary>(EMPTY_SUMMARY);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUltraCritical = useCallback(async () => {

    if (!auth.isAuthenticated || !auth.user?.access_token) {
      setSummary(EMPTY_SUMMARY);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await getUltraCriticalAlarms();
      if (!response.success) {
        const message = response.error?.message || 'Failed to load ultra critical alarms';
        const status = response.error?.status;
        setError(status ? `${message} (HTTP ${status})` : message);
        setSummary(EMPTY_SUMMARY);
      } else {
        setSummary(normalizeUltraCritical(response.data ?? null));
      }
    } catch (caughtError: unknown) {
      let message = 'Failed to load ultra critical alarms';
      if (caughtError instanceof Error && caughtError.message) {
        message = caughtError.message;
      }
      setError(message);
      setSummary(EMPTY_SUMMARY);
    } finally {
      setLoading(false);
    }
  }, [auth.isAuthenticated, auth.user?.access_token]);

  useEffect(() => {
    void fetchUltraCritical();
  }, [fetchUltraCritical]);

  useEffect(() => {
    return alarmAcknowledgedEvent.subscribe(() => {
      void fetchUltraCritical();
    });
  }, [fetchUltraCritical]);

  useEffect(() => {
    const handleRuleLifecycleChange = () => {
      void fetchUltraCritical();
    };

    const eventNames = ['rules:created', 'rules:updated', 'rules:deleted', 'rules:status-changed'];
    eventNames.forEach(eventName => {
      window.addEventListener(eventName, handleRuleLifecycleChange as EventListener);
    });

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void fetchUltraCritical();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      eventNames.forEach(eventName => {
        window.removeEventListener(eventName, handleRuleLifecycleChange as EventListener);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchUltraCritical]);

  return {
    ...summary,
    hasAny: summary.totalCount > 0,
    loading,
    error,
    refresh: () => {
      void fetchUltraCritical();
    },
  };
}
